import type { ReactNode } from 'react';

import useAppsHeaderOptionData from '@/hooks/apps/useAppsHeaderOptionData';

import MobileFooter from '../mobile/Footer';
import MobileHeader from '../mobile/Header';
import type { paddingProps as MobilePadding } from '../mobile/Main';
import DesktopFooter from '../pc/Footer';
import DesktopHeader from '../pc/Header';
import type { paddingProps as DesktopPadding } from '../pc/Main';

type BackgroundColor = 'white' | 'gray';

type Props = {
  children: ReactNode;
  desktopPadding?: DesktopPadding;
  mobilePadding?: MobilePadding;
  hideFooter?: boolean;
  hideMenu?: boolean;
  bgColor?: BackgroundColor;
};

const DEFAULT_DESKTOP_PADDING: DesktopPadding = {
  top: '0',
  bottom: '0',
};
const DEFAULT_MOBILE_PADDING: MobilePadding = {
  top: '0',
  bottom: '100px',
};

/**
 * padding 은 런타임 prop 이므로 Tailwind JIT 가 스캔할 수 있도록
 * 완성된 클래스명을 정적 맵으로 둔다.
 */
const MOBILE_PT: Record<MobilePadding['top'], string> = {
  '0': 'pt-0',
  '40px': 'pt-10',
};
const MOBILE_PB: Record<MobilePadding['bottom'], string> = {
  '0': 'pb-0',
  '100px': 'pb-[100px]',
};
const DESKTOP_PT: Record<DesktopPadding['top'], string> = {
  '0': 'lg:pt-0',
  '100px': 'lg:pt-[100px]',
};
const DESKTOP_PB: Record<DesktopPadding['bottom'], string> = {
  '0': 'lg:pb-0',
  '200px': 'lg:pb-[200px]',
};

export default function ResponsiveLayout({
  children,
  desktopPadding = DEFAULT_DESKTOP_PADDING,
  mobilePadding = DEFAULT_MOBILE_PADDING,
  hideFooter = false,
  hideMenu = false,
  bgColor = 'white',
}: Props) {
  const { isHeaderChanges } = useAppsHeaderOptionData();

  // variables.apps: mobile-header-height 60px, mobile-tabbar-height 60px, pc-header-height 100px
  const containerPadding = isHeaderChanges
    ? 'pt-0 pb-[60px] lg:pt-0 lg:pb-0'
    : 'pt-[60px] pb-[60px] lg:pt-[100px] lg:pb-0';

  return (
    <section className={`flex min-h-full flex-col ${containerPadding}`}>
      <div className="hidden lg:contents">
        <DesktopHeader hideMenu={hideMenu} />
      </div>
      <div className="contents lg:hidden">
        <MobileHeader hideMenu={hideMenu} />
      </div>

      {/*
        bgColor 기본값이 'white' 인데 예전에는 아무것도 칠하지 않아
        페이지 배경이 전적으로 캔버스에 의존했다. 명시적으로 칠한다.
      */}
      <main
        className={`flex-1 ${MOBILE_PT[mobilePadding.top]} ${MOBILE_PB[mobilePadding.bottom]} ${DESKTOP_PT[desktopPadding.top]} ${DESKTOP_PB[desktopPadding.bottom]} ${
          bgColor === 'gray' ? 'bg-apps-coreGray05' : 'bg-apps-coreGrayWhite'
        }`}
      >
        {children}
      </main>

      {!hideFooter && (
        <>
          <div className="hidden lg:contents">
            <DesktopFooter />
          </div>
          <div className="contents lg:hidden">
            <MobileFooter />
          </div>
        </>
      )}
    </section>
  );
}
